/* eslint-disable no-param-reassign */
/**
 * @Author: Roni Laukkarinen
 * @Date:   2021-09-01 11:55:37
 * @Last Modified by:   Roni Laukkarinen
 * @Last Modified time: 2022-10-31 16:23:45
 */
/**
 * Style external links
 */

//import getLocalization from './localization';

function isLinkExternal(link, localDomains) {
  // Empty links are not external
  if (!link.length) {
    return false;
  }

  const exceptions = [
    '#',
    'tel:',
    'mailto:',
    '/',
    'https://twitter.com',
    'https://x.com',
    'https://www.youtube.com',
    'https://www.facebook.com',
    'https://www.linkedin.com',
  ];

  // Check if the url starts with some of the exceptions
  const isException = exceptions.some((exception) => {
    const compare = new RegExp(`^${exception}`, 'g');
    return compare.test(link);
  });

  if (isException) {
    return false;
  }

  let linkUrl;
  try {
    linkUrl = new URL(link);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`Invalid URL: ${link}`);
    return false;
  }
  // Check if host is one of the local domains
  return !localDomains.some((domain) => linkUrl.host === domain);
}

/**
 * Try to get image alt texts from inside a link
 * to use in aria-label, when only elements inside
 * of link are images
 * @param {*} link DOM link element
 * @returns string
 */
function getChildAltText(link) {
  const children = [...link.children];

  if (children.length === 0) {
    return '';
  }

  const childImgs = children.filter(
    (child) => child.tagName.toLowerCase() === 'img'
  );

  // If there are other elements than img elements, no need to add aria-label
  if (children.length !== childImgs.length) {
    return '';
  }

  // Find alt texts and add to array
  const altTexts = childImgs
    .filter((child) => child.alt && child.alt !== '')
    .map((child) => child.alt);

  // If there is no alt texts,
  if (!altTexts.length) {
    return '';
  }

  return altTexts.join(', ');
}

function styleExternalLinks() {
  let localDomains = [window.location.host];

  if (typeof window.air_light_externalLinkDomains !== 'undefined') {
    localDomains = localDomains.concat(window.air_light_externalLinkDomains);
  }

  const links = document.querySelectorAll('a');

  const externalLinks = [...links].filter((link) =>
    isLinkExternal(link.href, localDomains)
  );

  // eslint-disable-next-line consistent-return
  externalLinks.forEach((externalLink) => {
    // Abort mission if there is only img element inside of link
    if (
      externalLink.childElementCount === 1 &&
      externalLink.children[0].tagName.toLowerCase() === 'img'
    ) {
      return false;
    }

    if (!externalLink.classList.contains('no-external-link-label')) {
      const textContent = externalLink.textContent.trim().length
        ? externalLink.textContent.trim()
        : getChildAltText(externalLink);
      const ariaLabel =
        externalLink.target === '_blank'
          ? `${textContent}: ${air_light_screenReaderText.external_link}, ${air_light_screenReaderText.target_blank}`
          : `${textContent}: ${air_light_screenReaderText.external_link}`;
      externalLink.setAttribute('aria-label', ariaLabel);
    }

    // Arrow icon won't be added if one of these classes is defined for the link
    const classExceptions = [
      'no-external-link-indicator',
      'global-link',
      'button',
      'c-button',
    ];

    if (
      !classExceptions.some((className) =>
        externalLink.classList.contains(className)
      )
    ) {
      // Add SVG arrow icon
      externalLink.insertAdjacentHTML(
        'beforeend',
        '<svg class="external-link" width="24" height="24" viewBox="0 0 24 24" fill="inherit" xmlns="http://www.w3.org/2000/svg"><path d="M16.571 12.4591V15.3759C16.571 16.8249 15.419 18.001 13.9996 18.001H6.57134C5.15196 18.001 4 16.8249 4 15.3759V7.79222C4 6.34316 5.15196 5.16711 6.57134 5.16711H12.8568C13.0174 5.16711 13.1425 5.29486 13.1425 5.45879V6.04214C13.1425 6.20607 13.0174 6.33382 12.8568 6.33382H6.57134C5.78565 6.33382 5.14282 6.9901 5.14282 7.79222V15.3759C5.14282 16.178 5.78565 16.8343 6.57134 16.8343H13.9996C14.7853 16.8343 15.4282 16.178 15.4282 15.3759V12.4591C15.4282 12.2952 15.5533 12.1674 15.7139 12.1674H16.2853C16.4458 12.1674 16.571 12.2952 16.571 12.4591ZM19.9994 4.58375V9.25061C19.9994 9.56971 19.7406 9.83397 19.428 9.83397C19.276 9.83397 19.1332 9.77038 19.0263 9.66071L17.4549 8.05648L11.634 13.9991C11.5803 14.054 11.5003 14.0901 11.4289 14.0901C11.3575 14.0901 11.2769 14.0534 11.2237 13.9991L10.2061 12.9602C10.1524 12.9053 10.1169 12.8237 10.1169 12.7508C10.1169 12.6778 10.1529 12.5956 10.2061 12.5413L16.027 6.59867L14.4556 4.99443C14.3488 4.88535 14.2859 4.73892 14.2859 4.58433C14.2859 4.26524 14.5448 4.00098 14.8573 4.00098H19.4286C19.7412 4.00098 20 4.26524 20 4.58433L19.9994 4.58375Z" fill="inherit"/></svg>'
        //'<svg class="external-link-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 9 9"><path d="M4.499 1.497h4v4m0-4l-7 7" fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
      );
    }
  });
}

function initExternalLinkLabels() {
  // Add aria-labels to links without text or aria-labels and contain image with alt text
  const links = [...document.querySelectorAll('a')];
  // eslint-disable-next-line no-unused-vars
  const linksWithImgChildren = links.forEach((link) => {
    // If link already has text content or an aria label no need to add aria-label
    if (link.textContent.trim() !== '' || link.ariaLabel) {
      return;
    }

    const ariaLabel = getChildAltText(link);
    if (ariaLabel !== '') {
      link.ariaLabel = ariaLabel;
    }
  });
}

document.addEventListener('DOMContentLoaded', function (event) {
  styleExternalLinks();
  initExternalLinkLabels();
});
